import { DateTime } from 'luxon';
import { cloneDeep } from 'lodash';

const isProduction = process.env.NODE_ENV === 'production';

const createLogger = () => {
  const log = (level: string, moduleName: string, message: string) => {
    // Don't log in production
    if (isProduction) {
      return;
    }

    const fullMessage = `${DateTime.local().toISO()} [${moduleName}] ${level}: ${message}`;
    switch (level) {
      case 'DEBUG':
        console.debug(fullMessage);
        break;
      case 'INFO':
        console.info(fullMessage);
        break;
      case 'WARN':
        console.warn(fullMessage);
        break;
      default:
        console.error(fullMessage);
        break;
    }
  };

  return {
    debug: (moduleName: string, message: string) => {
      log('DEBUG', moduleName, message);
    },

    info: (moduleName: string, message: string) => {
      log('INFO', moduleName, message);
    },

    warn: (moduleName: string, message: string) => {
      log('WARN', moduleName, message);
    },

    error: (moduleName: string, messageTitle: string, messageContent?: string, e?: unknown) => {
      let translatedTitle = messageTitle;
      let translatedContent = messageContent;
      if (messageTitle.length > 50) {
        translatedTitle = `${messageTitle.substring(0, 50)}...`;
      }

      if (!messageContent) {
        // Pick up the message from the exception instead if available
        if (e instanceof Error && e.message) {
          translatedContent = `Exception message: ${e.message}`;
        } else if (e instanceof Object || e instanceof Array) {
          translatedContent = `Exception object: ${JSON.stringify(e)}`;
        } else {
          translatedContent = `Exception content: ${e}`;
        }
      }
      const jsError = new Error(translatedContent);
      jsError.name = `[${moduleName}] ${translatedTitle}`;

      if (e && (e as Error).stack) {
        jsError.stack = (e as Error).stack;
      } else {
        jsError.stack = translatedContent;
      }

      console.error(`${DateTime.local().toISO()} [${moduleName}] ${translatedContent}`, jsError);
    },
  };
};

export const logger = createLogger();

const isObject = (obj: any) => obj && typeof obj === 'object' && !Array.isArray(obj);

function setDeep(
  object: Record<string, unknown>,
  keyName: string,
  value: any,
  seen: WeakSet<object>
) {
  if (seen.has(object)) {
    return;
  }
  seen.add(object);

  Object.keys(object).forEach((key) => {
    if (isObject(object[key])) {
      setDeep(object[key] as Record<string, unknown>, keyName, value, seen);
    } else if (key === keyName) {
      object[key] = value;
    }
  });
}

export const sanitize = (object: any) => {
  if (!object || typeof object !== 'object') {
    return object;
  }
  const result = cloneDeep(object);
  setDeep(result, 'Authorization', 'Bearer ***', new WeakSet());
  setDeep(result, 'access_token', '***', new WeakSet());
  setDeep(result, 'image_url', '***', new WeakSet());
  return result;
};
